import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * Recursively finds all shadow roots within an element or shadow root
 */
function getAllShadowRoots(root: HTMLElement | ShadowRoot, shadowRoots: Set<ShadowRoot> = new Set()): ShadowRoot[] {
    // Determine the root to query
    let queryRoot: Document | ShadowRoot | HTMLElement;

    if (root instanceof ShadowRoot) {
        queryRoot = root;
        shadowRoots.add(root);
    } else {
        // If it's an HTMLElement, check if it has a shadow root
        if (root.shadowRoot) {
            shadowRoots.add(root.shadowRoot);
            queryRoot = root.shadowRoot;
        } else {
            // No shadow root, query the element itself to find nested shadow DOMs
            queryRoot = root;
        }
    }

    // Find all elements with shadow roots within this root
    const allElements = queryRoot.querySelectorAll('*');
    allElements.forEach((el) => {
        if (el.shadowRoot && !shadowRoots.has(el.shadowRoot)) {
            shadowRoots.add(el.shadowRoot);
            // Recursively find nested shadow roots
            getAllShadowRoots(el.shadowRoot, shadowRoots);
        }
    });

    return Array.from(shadowRoots);
}

export function useShadowRootCustomStyles<TElement extends HTMLElement | undefined | null>(elementRef: RefObject<TElement>, customStyles: string) {
    useEffect(() => {
        if (!elementRef.current) return;

        const styleElements: HTMLStyleElement[] = [];

        const injectStyles = () => {
            if (!elementRef.current) return;

            // Find all shadow roots (including nested ones)
            const shadowRoots = getAllShadowRoots(elementRef.current);

            // Inject styles into all shadow roots
            shadowRoots.forEach((shadowRoot) => {
                // Check if style already exists to avoid duplicates
                const existingStyle = Array.from(shadowRoot.querySelectorAll('style')).find((style) => style.innerHTML === customStyles);

                if (!existingStyle) {
                    const itemStyle = document.createElement('style');
                    itemStyle.innerHTML = customStyles;
                    shadowRoot.appendChild(itemStyle);
                    styleElements.push(itemStyle);
                }
            });
        };

        // Initial injection
        injectStyles();

        // Also inject after a small delay to catch shadow DOMs created asynchronously
        const timeoutId = setTimeout(() => {
            injectStyles();
        }, 100);

        // Cleanup: remove style elements when component unmounts or dependencies change
        return () => {
            clearTimeout(timeoutId);
            styleElements.forEach((style) => {
                if (style.parentNode) {
                    style.parentNode.removeChild(style);
                }
            });
        };
    }, [elementRef, customStyles]);
}
